import { inject, Injectable } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { io, Socket } from 'socket.io-client';
import { IUser, ToastType } from '../../models';
import { Observable, Subject, Subscription } from 'rxjs';
import { Store } from '@ngrx/store';
import { AppState } from '../../state/root';
import { selectLoggedInUser } from '../../state/user/user.selectors';
import { ToastService } from '../toast/toast.service';
import { LocalStorageService } from '../local-storage/local-storage.service';
import { AlertPayload, SocketEnvelope } from '../../models/alert-payload.interface';

type SocketEvent = "connect" | "reconnect" | "connect_error" | "reconnect_error" | "disconnect" | "error" | "reconnect_attempt"
export type SystemAlertType = "CAMERA_DOWN"

export interface ISystemAlert {
  trackId: string,
  type: SystemAlertType
  camera_name?: string
  site_name?: string,
  camera_id?: string,
  timestamp?: string
}

@Injectable({
  providedIn: 'root'
})
export class SystemAlertsService {
  private httpClient = inject(HttpClient);
  private store = inject(Store<AppState>);
  private toastService = inject(ToastService);
  private localStorageService = inject(LocalStorageService);

  readonly events = {
    cameraDown: "_health_monitor"
  }
  readonly endpoints = {
    socket: `${environment.alertSocketUrl}:${environment.alertSocketPort}`,
    getOfflineCameras: `${environment.apiUrl}/CameraAPI/GetOfflineCameras`
  }

  private downCameras = new Set<string>();
  private socket!: Socket;
  public notifications = new Subject<ISystemAlert>()
  private pollingInterval: NodeJS.Timeout | null = null;
  private fetchOfflineCameras!: () => void;
  private subscriptions: Subscription[] = [];

  public initialise() {
    const options = this.getSocketOptions()
    this.socket = io(this.endpoints.socket, options)
    const user = this.store.selectSignal(selectLoggedInUser)()
    this.listenToSocketConnectionEvents();
    this.listenOnNewCameraDownSocket(user.organisation_id)

    this.fetchOfflineCameras = () => this.fetchOfflineCamerasLogic(user)
    this.fetchOfflineCameras()
  }

  private fetchOfflineCamerasLogic(user: IUser) {
    const offlineSub = this.getOfflineCamera(user)?.subscribe(response => {
      response?.data.forEach(camera => {
        const id = camera.camera_id
        if (id == null || this.downCameras.has(id)) {
          return
        }
        this.downCameras.add(id)
        this.pushNotification("CAMERA_DOWN", camera)
      })
    });

    if(offlineSub != null) {
      this.subscriptions.push(offlineSub);
    }
  }

  private getSocketOptions() {
    const token = this.localStorageService.getItem(environment.authTokenKey);

    if(token == null) {
      console.error('System Notification, invalid token');
    }

    return {
      'reconnectionDelayMax': 20000,
      'reconnectionDelay': 2500,
      transportOptions: {
        polling: {
          extraHeaders: { "Authorization": token },
        },
      }
    }
  }


  private bindEvent(event: SocketEvent, connected: boolean | undefined = undefined) {
    if (this.socket == null) {
      return;
    }

    this.socket!.on(event, () => {
      if (connected === true && this.pollingInterval != null) {
        clearInterval(this.pollingInterval)
      }
      if (connected === false && this.pollingInterval == null) {
        this.pollingInterval = this.startPolling()
      }
    })
  }

  private startPolling() {
    return setInterval(() => {
      this.fetchOfflineCameras()
    }, 10000)
  }

  private listenToSocketConnectionEvents() {
    this.bindEvent("connect", true)
    this.bindEvent("reconnect", true)
    this.bindEvent("connect_error", false)
    this.bindEvent("disconnect", false)
    this.bindEvent("error", false)
  }

  private pushNotification(type: SystemAlertType, data: AlertPayload) {
    this.notifications.next({
      type: type,
      trackId: crypto.randomUUID(),
      ...data
    })
  }

  private listenOnNewCameraDownSocket(organisationID: string) {
    if (this.socket == null) {
      return;
    }

    this.socket.on(`${organisationID}${this.events.cameraDown}`, (envelope: SocketEnvelope | null | undefined) => {
      const cameraData = envelope?.data
      const id = cameraData?.camera_id
      if (cameraData == null || id == null || this.downCameras.has(id)) {
        return
      }
      this.downCameras.add(id)
      this.toastService.showToast(`${cameraData.camera_name} is down at ${cameraData.site_name}!`, ToastType.ERROR)
      this.pushNotification("CAMERA_DOWN", cameraData)
    })
  }

  getOfflineCamera(user: IUser): Observable<{ data: AlertPayload[] }> | void {
    if (user == undefined || user.id == undefined) {
      return;
    }
    const body = {
      user: user.id,
      organisation_id: user.organisation_id
    }
    return this.httpClient.post<{ data: AlertPayload[] }>(this.endpoints.getOfflineCameras, body);
  }

  destroy() {
    this.socket?.disconnect();
    this.socket?.close();
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    if(this.pollingInterval != null) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
  }
}
